import { ErrorCode, HttpError, INVALID_DELEGATION_ERROR_NAME, SESSION_EXPIRY_ERROR_NAME, } from "../domain";
// Imported from the module rather than the barrel: `../domain` cycles back through here, so a
// binding read at module scope (as below) is not yet initialised when going via the barrel
import { ICErrorCode } from "../domain/error";
import { parseBigInt } from "./bigint";
export function isError(value) {
    return value != null && typeof value === "object" && "kind" in value && value.kind === "error";
}
// Request kinds whose failures the caller always tolerates (e.g. `refreshAccountBalance`
// resolves to undefined, keeping the cached balance).
const callerToleratedErrorKinds = new Set(["refreshAccountBalance"]);
// A ledger canister that is frozen (IC0207), has no wasm module (IC0537) or has been deleted
// (IC0301) is a dead / decommissioned token ledger. Balance refreshes against these are
// expected: for the ~30 day window before the IC uninstalls a frozen canister, and until the
// registry's uninstalled-token detection + client cache purge remove the token. Any OTHER failure
// is a real signal.
const DEAD_LEDGER_ERROR_CODES = [
    ICErrorCode.CanisterOutOfCycles,
    ICErrorCode.CanisterNotFound,
    ICErrorCode.CanisterWasmModuleNotFound,
];
function isDeadLedgerError(error) {
    return (error instanceof HttpError &&
        error.rejectErrorCode !== undefined &&
        DEAD_LEDGER_ERROR_CODES.includes(error.rejectErrorCode));
}
// The session ending underneath an in-flight request is expected: the user logged out or their
// delegation expired and the app redirects to login. Every request racing that teardown fails
// with one of these, so reporting them buries real regressions under per-kind noise.
function isExpectedSessionError(error) {
    if (error == null || typeof error !== "object" || !("name" in error))
        return false;
    return error.name === "AnonymousOperationError" || requiresLogout(error);
}
// Network weather as seen from the client: a gateway 502/503/504, or a fetch that never got a
// response at all. The client retries or surfaces these contextually and server-side monitoring
// owns the underlying incidents, so per-occurrence client reports are pure noise. 500 is NOT
// included: replica rejections (including canister traps) map to HttpError 500 here, and those
// are exactly the signal this filter must keep.
// "error decoding response body" is the Tauri (reqwest) equivalent of a fetch dying mid-body.
const NETWORK_NOISE_PATTERN = /failed to fetch|networkerror|load failed|network connection was lost|error decoding response body/i;
const REQWEST_NOISE_PATTERN = /error decoding response body/i;
const RETRY_EXHAUSTED_PATTERN = /retry strategy exhausted after \d+ attempts/i;
const AGENT_FETCH_FAILED_PATTERN = /^failed to fetch http request/i;
const AGENT_UNEXPECTED_ERROR_PATTERN = /^unexpected error: /i;
// The boundary node had no replica to route to and answered 500 itself (#32147, #32148)
const NO_HEALTHY_ROUTES_PATTERN = /no healthy routes available/i;
// Every HttpError subclass overwrites `name` with its own, so a bare `name === "HttpError"` test
// misses them. `code` only means an HTTP status on one of these.
const HTTP_ERROR_NAMES = new Set([
    "HttpError",
    "AuthError",
    "DestinationInvalidError",
    "CanisterUnavailableError",
    "CanisterMethodNotFoundError",
    "ResponseTooLargeError",
]);
function isTransientNetworkError(error) {
    // Structural checks rather than instanceof: errors which crossed the worker boundary
    // arrive as plain objects where only name/message/code survive
    const name = errorName(error);
    if (HTTP_ERROR_NAMES.has(name)) {
        // 503 also covers CanisterUnavailableError: a frozen or uninstalled canister cannot
        // recover inside one request, and server-side monitoring owns the incident.
        const code = Number(error.code);
        if (code >= 502 && code <= 504)
            return true;
        // The same weather as a 502-504 but sent as a 500. None of our canisters' reject text
        // contains it, so the 500s this filter must keep still report.
        if (NO_HEALTHY_ROUTES_PATTERN.test(errorMessage(error)))
            return true;
    }
    const message = errorMessage(error);
    // Exempt from the TypeError rule below because it only ever originates from the Tauri
    // native fetch layer
    if (REQWEST_NOISE_PATTERN.test(message))
        return true;
    // The IC agent gave up after its fetch retries: every attempt failed at the transport
    // layer (a replica rejection is thrown immediately, without retrying)
    if (name === "HttpError" && RETRY_EXHAUSTED_PATTERN.test(message))
        return true;
    // The agent wraps a fetch that threw (no response at all) as an HttpError with this prefix
    // and the browser's own text after it. Same network weather, different envelope.
    if (HTTP_ERROR_NAMES.has(name) && AGENT_FETCH_FAILED_PATTERN.test(message))
        return true;
    // The agent's catch-all for anything it doesn't recognise, here a fetch that threw (#31999)
    if (HTTP_ERROR_NAMES.has(name) &&
        AGENT_UNEXPECTED_ERROR_PATTERN.test(message) &&
        NETWORK_NOISE_PATTERN.test(message))
        return true;
    // Only for the browser's own TypeError: our code also throws Errors whose text happens to
    // start "Failed to fetch ...", and those must stay reportable. A bare string carries no
    // name, so it can never satisfy this and is reported like any other unrecognised failure.
    return name === "TypeError" && NETWORK_NOISE_PATTERN.test(message);
}
// Failures produced by the client's environment rather than our code: corrupt or exhausted
// browser storage, permission prompts the user declined or let time out, and the browser
// failing to fetch its own service worker script. Nothing to fix per-occurrence.
const ENVIRONMENT_NOISE_PATTERNS = [
    /internal error opening backing store/i,
    /file_error_no_space/i,
    /failed to read large indexeddb value/i,
    /failed to write blobs/i,
    /database connection is closing/i,
    /connection is closing because of: io error/i,
    /denied permission to use service worker/i,
    // WebAuthn's blanket NotAllowedError: the user dismissed the passkey prompt or it timed out
    /operation either timed out or was not allowed/i,
    // The platform's passkey service itself failing: Android's credential manager (#30432) or a
    // browser with no WebAuthn service to talk to (#31128)
    /error connecting to web authentication service/i,
    /unknown error occurred while talking to the credential manager/i,
    /failed to (update|register) a serviceworker/i,
    // IndexedDB backing store failures (UnknownError) seen in storms from broken iOS installs
    /failed to delete record from object store/i,
    /unable to store record in object store/i,
    // "Attempt to delete range / get a record / open a cursor / get an index record ..." (#27293,
    // #10401): the same failure whichever operation happened to hit it
    /database without an in-progress transaction/i,
    // Chrome's IndexedDB lost the blob file behind a record (#28921)
    /data lost due to missing file/i,
    // Safari / Firefox-on-iOS dropping the IndexedDB connection; only a reload recovers it
    /connection to indexed database server lost/i,
    // Safari's IndexedDB failing internally, or the user (or the OS reclaiming space) wiping
    // the site's storage from under an open connection
    /internal error was encountered in the indexed database server/i,
    /database deleted by request of the user/i,
    // Chrome's form of the same: the site's storage was deleted under an open connection, by the
    // user clearing site data or the browser evicting it (#29538)
    /force close delete origin/i,
    // Safari's in-app browser bridge complaining about its own injected script
    /wkwebview api client did not respond to this postmessage/i,
    // The client's clock is wrong, so the replica certificate looks like it is from the future;
    // or the device slept mid-request and the certificate is stale by the time it is checked
    /certificate is signed more than 5 minutes in the (future|past)/i,
    // The agent re-synced its clock with the IC and the certificate still doesn't fit (#31681)
    /certificate is still too far in the future/i,
    // The agent gave up polling for an update's result: the network, not our code
    /request timed out after \d+ msec/i,
    /backoff strategy exhausted/i,
    // Two tabs on different IndexedDB schema versions: the older one's transaction names a
    // store the upgrade removed. Resolves itself on reload.
    /one of the specified object stores was not found/i,
    // The same wrong clock seen from the other side: the ingress expiry the agent computed from
    // Date.now() falls outside the window the replica will accept. Devices weeks or months out of
    // date produce these in storms, and because the replica echoes the timestamps back in the
    // message, every skewed device mints a new error item rather than joining an existing one.
    /invalid request expiry/i,
    // Safari's built-in media controls script, no frame of ours involved
    /can't find variable: EmptyRanges/i,
    // Benign browser warning surfaced as an error event
    /resizeobserver loop/i,
    // The IC's Bitcoin canister trapping while the Bitcoin API is switched off: an IC-side
    // incident that every background BTC balance refresh hits until it is switched back on (#31771)
    /bitcoin api is disabled/i,
    // The same canister trapping while it catches up with the Bitcoin network. None of our
    // canisters use this text (#31771)
    /canister state is not fully synced/i,
    // A canister stopped for an upgrade rejects every call until it restarts (#31692, #31764).
    // The upgrader restarts it whether or not the install succeeded, and logs a failed start.
    /error code: IC0508\b/i,
    // emoji-picker-element getting a 5xx back from the jsDelivr CDN for its emoji data (#31998):
    // the CDN's outage, not our code. A 4xx would mean the data URL itself broke, so it reports.
    /emoji-picker-element-data\S*:\s+5\d\d\b/i,
    // A request reaching the worker before its agent exists or after it has gone, around login
    // and logout. The worker leaves it unreported, but the caller sees it as a rejection (#9757)
    /worker has no agent to handle request/i,
    // The ledger refusing an external wallet's approval because the account the user picked
    // doesn't hold enough (#32090). The payment form shows the failure; any other refusal reports.
    /^approval failed: insufficientfunds\b/i,
    // ICRC-25's "Action aborted" (code 3001): the user turned the request down in their wallet
    // (#32125). The payment form shows the failure; other signer errors still report.
    /^action aborted$/i,
];
function errorName(error) {
    if (error == null || typeof error !== "object" || !("name" in error))
        return "";
    return typeof error.name === "string" ? error.name : "";
}
function errorMessage(error) {
    if (typeof error === "string")
        return error;
    if (error == null || typeof error !== "object" || !("message" in error))
        return "";
    return typeof error.message === "string" ? error.message : "";
}
// The throw site is browser-extension code (wallet inpage scripts, injected wasm hitting our
// CSP, ...): not ours to fix. Rollbar's checkIgnore does the same from the payload frames for
// uncaught errors that bypass the logger; this covers the ones the window error handler routes
// through the logger, where only the Error (and its stack string) is available.
const EXTENSION_FRAME_PATTERN = /^\s*(at .*|.*@)(chrome|moz|safari-web)-extension:\/\//;
function thrownByExtension(error) {
    if (error == null || typeof error !== "object" || !("stack" in error))
        return false;
    if (typeof error.stack !== "string")
        return false;
    // V8 puts "Name: message" on the first line and frames below ("    at fn (url)"); JSC and
    // Gecko start with frames ("fn@url"). The throw site is the first frame that names a script:
    // a builtin throwing on the extension's behalf (Object.defineProperty, JSON.parse, ...) shows
    // up first as "(<anonymous>)" or "[native code]" and says nothing about whose code it was.
    const frame = error.stack
        .split("\n")
        .find((line) => /^\s*at |@/.test(line) && /[a-z-]+:\/\//i.test(line));
    return frame !== undefined && EXTENSION_FRAME_PATTERN.test(frame);
}
function isEnvironmentNoise(error) {
    const name = errorName(error);
    // AbortError is always a deliberate cancellation (navigation, stream teardown);
    // QuotaExceededError is the client's disk, not our code
    if (name === "AbortError" || name === "QuotaExceededError")
        return true;
    return ENVIRONMENT_NOISE_PATTERNS.some((p) => p.test(errorMessage(error)));
}
// Events requests race membership changes: a user who has left, lapsed or been blocked keeps
// requesting events until local state catches up, and the server answers with a NotAuthorized
// code (100-106) which `assertSuccessfulEventsResponse` turns into a thrown Error embedding the
// response JSON. Expected client state, not a defect.
// ChatNotFound is the same race for a chat that no longer exists on the server (a deleted
// direct chat partner, say) while the local summary still does, and ThreadNotFound the same for
// a thread whose root message was deleted, or whose first reply has not reached the server yet.
// Deliberately scoped to that one message: a NotAuthorized code reaching us from anywhere else -
// a mutation, say - means our local view of the user's permissions is wrong, which is a defect.
const EVENTS_RESPONSE_ERROR_PREFIX = "Events response error:";
function isExpectedAccessError(error) {
    const message = errorMessage(error);
    if (!message.startsWith(EVENTS_RESPONSE_ERROR_PREFIX))
        return false;
    const match = message.match(/"code":(\d+)/);
    if (match == null)
        return false;
    const code = Number(match[1]);
    return ((code >= ErrorCode.InitiatorNotFound && code <= ErrorCode.InitiatorBlocked) ||
        code === ErrorCode.ChatNotFound ||
        code === ErrorCode.ThreadNotFound);
}
// Central filter applied by the logger before anything reaches Rollbar: expected session
// teardown, network weather, client-environment failures and membership races are dropped;
// everything else is reported.
export function shouldReportError(error) {
    return !(isExpectedSessionError(error) ||
        isTransientNetworkError(error) ||
        isEnvironmentNoise(error) ||
        isExpectedAccessError(error) ||
        thrownByExtension(error));
}
// The same filter for Rollbar's checkIgnore hook, where only the payload strings are available -
// i.e. errors captured by captureUncaught / captureUnhandledRejections which bypass our logger
// entirely. Rebuilds the structural shape `shouldReportError` inspects from the exception class
// and message so both paths share one rule set; `name` is empty for payloads that carry no
// exception. An HttpError's status only survives in its message on this path.
export function shouldReportMessage(name, message) {
    const status = message.match(/Status: (\d{3})\b/);
    const code = status != null ? Number(status[1]) : undefined;
    return shouldReportError({ name, message, code });
}
// Decide whether a failed worker request should be reported to our error tracker. Expected
// failures (session teardown, network weather, and dead-ledger errors for tolerated kinds) are
// silenced; every other failure - a decode error, a code bug - is still reported so real
// regressions stay visible.
export function shouldReportWorkerError(kind, error) {
    if (!shouldReportError(error))
        return false;
    return !(callerToleratedErrorKinds.has(kind) && isDeadLedgerError(error));
}
// Whether a rejected promise means the user's session is no longer usable and they must be logged
// out. These errors reach the client through the worker, so their prototype is gone and `name` is
// all that survives - see the constants in ../domain/error.
export function requiresLogout(error) {
    if (error == null || typeof error !== "object" || !("name" in error))
        return false;
    return error.name === SESSION_EXPIRY_ERROR_NAME || error.name === INVALID_DELEGATION_ERROR_NAME;
}
export function pinNumberFailureFromError(error) {
    function nextRetryAt(message) {
        if (message === undefined)
            return BigInt(0);
        const delay = parseBigInt(message);
        return delay !== undefined ? BigInt(Date.now()) + delay : BigInt(0);
    }
    switch (error.code) {
        case ErrorCode.PinRequired:
            return { kind: "pin_required" };
        case ErrorCode.PinIncorrect:
            return {
                kind: "pin_incorrect",
                nextRetryAt: nextRetryAt(error.message),
            };
        case ErrorCode.TooManyFailedPinAttempts:
            return {
                kind: "too_main_failed_pin_attempts",
                nextRetryAt: nextRetryAt(error.message),
            };
        default:
            return undefined;
    }
}
